<template>
  <div class="calendar-container">
    <div class="calendar-header">
      <h2>點名表</h2>
      <div class="view-controls">
        <button @click="setView('monthly')" :class="{ active: currentView === 'monthly' }">Monthly</button>
        <button @click="setView('weekly')" :class="{ active: currentView === 'weekly' }">Weekly</button>
        <button @click="setView('daily')" :class="{ active: currentView === 'daily' }">Daily</button>
      </div>
      <div class="date-navigation">
        <button @click="previousPeriod">←</button>
        <h3>{{ currentPeriodTitle }}</h3>
        <button @click="nextPeriod">→</button>
      </div>
    </div>

    <div class="calendar-view">
      <MonthlyView 
        v-if="currentView === 'monthly'" 
        :sessions="sessions" 
        :currentDate="currentDate"
        @session-click="handleSessionClick"
      />
      <WeeklyView 
        v-if="currentView === 'weekly'" 
        :sessions="sessions" 
        :currentDate="currentDate"
        @session-click="handleSessionClick"
      />
      <DailyView 
        v-if="currentView === 'daily'" 
        :sessions="sessions" 
        :currentDate="currentDate"
        @session-click="handleSessionClick"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useQuery } from '@vue/apollo-composable';
import { GET_CALENDAR_SESSIONS } from '../graphql/queries';
import MonthlyView from './calendar/MonthlyView.vue';
import WeeklyView from './calendar/WeeklyView.vue';
import DailyView from './calendar/DailyView.vue';

const router = useRouter();

const currentView = ref('daily');
const currentDate = ref(new Date());
const selectedSession = ref(null);

const { result, loading, error, refetch } = useQuery(
  GET_CALENDAR_SESSIONS, 
  () => ({
    startDate: getPeriodStart(),
    endDate: getPeriodEnd(),
    viewType: currentView.value.toUpperCase(),
  }),
  { fetchPolicy: 'network-only' }
);

const sessions = computed(() => result.value?.calendarSessions || []);

function normalizeDateStart(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function normalizeDateEnd(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function getPeriodStart() {
  const date = new Date(currentDate.value);
  if (currentView.value === 'monthly') {
    date.setDate(1);
    return normalizeDateStart(date);
  } else if (currentView.value === 'weekly') {
    // Get Monday of the current week
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust when Sunday
    date.setDate(diff);
    return normalizeDateStart(date);
  } else {
    return normalizeDateStart(date);
  }
}

function getPeriodEnd() {
  const date = new Date(currentDate.value);
  if (currentView.value === 'monthly') {
    date.setMonth(date.getMonth() + 1);
    date.setDate(0);
    return normalizeDateEnd(date);
  } else if (currentView.value === 'weekly') {
    // Get Sunday of the current week
    const start = getPeriodStart();
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return normalizeDateEnd(end);
  } else {
    return normalizeDateEnd(date);
  }
}

const currentPeriodTitle = computed(() => {
  if (currentView.value === 'monthly') {
    return currentDate.value.toLocaleDateString('default', { month: 'long', year: 'numeric' });
  } else if (currentView.value === 'weekly') {
    const start = getPeriodStart();
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  } else {
    return currentDate.value.toLocaleDateString();
  }
});

function setView(view) {
  currentView.value = view;
}

function previousPeriod() {
  const date = new Date(currentDate.value);
  if (currentView.value === 'monthly') {
    date.setMonth(date.getMonth() - 1);
  } else if (currentView.value === 'weekly') {
    date.setDate(date.getDate() - 7);
  } else {
    date.setDate(date.getDate() - 1);
  }
  currentDate.value = date;
}

function nextPeriod() {
  const date = new Date(currentDate.value);
  if (currentView.value === 'monthly') {
    date.setMonth(date.getMonth() + 1);
  } else if (currentView.value === 'weekly') {
    date.setDate(date.getDate() + 7);
  } else {
    date.setDate(date.getDate() + 1);
  }
  currentDate.value = date;
}

function handleSessionClick(session) {
  router.push(`/session/${session.session_id}/attendance`);
}

watch([currentView, currentDate], () => {
  refetch({
    startDate: getPeriodStart(),
    endDate: getPeriodEnd(),
    viewType: currentView.value.toUpperCase()
  });
});
</script>

<style scoped>
.calendar-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
}

.view-controls {
  display: flex;
  gap: 10px;
}

.view-controls button {
  padding: 5px 15px;
  border: 1px solid #ddd;
  background: #f5f5f5;
  border-radius: 4px;
  cursor: pointer;
}

.view-controls button.active {
  background: #4CAF50;
  color: white;
  border-color: #45a049;
}

.date-navigation {
  display: flex;
  align-items: center;
  gap: 15px;
}

.date-navigation button {
  padding: 5px 10px;
  border: 1px solid #ddd;
  background: #f5f5f5;
  border-radius: 4px;
  cursor: pointer;
}

.calendar-view {
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
}
</style>